/**
 * Shared font + line-spacing tokens for the magic report PDF export.
 *
 * Every export builder (REPORT_STYLES in export.util.ts, the section/action-item table builders,
 * the notes templates) pulls its font family, table font size and table row spacing from here, so a
 * spacing or font change is made in one place instead of grepping for hardcoded values.
 */

/** Single-quoted so it can be dropped into inline style="..." attributes as well as stylesheets. */
export const REPORT_FONT_FAMILY = "'Times New Roman', serif";

/** Body size of the portrait-page data tables (change orders, schedule dates, lien waivers, ...). */
export const REPORT_TABLE_FONT_SIZE = '16px';

/** Inline style for the portrait-page data tables built by the section and action-item builders. */
export const REPORT_TABLE_STYLE = `border-collapse: collapse; margin: 0 0 0.5em 0; width: 100%; border: none; font-family: ${REPORT_FONT_FAMILY}; font-size: ${REPORT_TABLE_FONT_SIZE};`;

/**
 * CSS line-height that the report treats as "single" spacing. Spacing multipliers below are
 * expressed the way the report reviewers ask for them (Word-style 1.15 / 1.5 spacing) and are
 * scaled off this value, so "1.5 spacing" means 1.5x a single-spaced row, not `line-height: 1.5`.
 */
const SINGLE_SPACED_LINE_HEIGHT = 1.2;

const lineSpacing = (multiplier: number): number => Math.round(SINGLE_SPACED_LINE_HEIGHT * multiplier * 100) / 100;

export const REPORT_LINE_HEIGHT = {
  /** Default for every table except the G703 continuation sheet. */
  TABLE: lineSpacing(1.15),
  /** G702 budget summary — both the Application for Payment Review Form page and section 2.1. */
  G702_SUMMARY: lineSpacing(1.5),
  /** Key schedule dates table (Schedule section + its Action Items copy). */
  SCHEDULE_DATES: lineSpacing(1.5),
  /** G703 continuation sheet keeps its own tuned, tight spacing (literal CSS values). */
  G703_HEADER: 1.1,
  G703_ROW: 1.15,
} as const;

/** Extra space under a table's header row so it stands apart from the first data row (non-G703 tables). */
export const REPORT_TABLE_HEADER_GAP = '0.5em';

/** Class hooks REPORT_STYLES targets for the tables that get non-default spacing. */
export const REPORT_TABLE_CLASS = {
  SCHEDULE_DATES: 'schedule-dates-table',
} as const;
